import { queryKeys } from "@/services/react-query.service/query-keys";
import { GHG_ADMINISTRATION_METHODOLOGY_ID } from "@ark/shared";
import { InvalidateQueryFilters, Query, QueryClient, QueryFilters } from "@tanstack/react-query";

import { refreshMunicipalPropertyCatalog } from "./refresh-municipal-property-catalog";

export async function refreshMunicipalPropertyConsumption({
  queryClient,
  scope: { workspaceId, propertyIdList },
  throwOnError = false,
}: {
  queryClient: QueryClient;
  scope: Parameters<typeof refreshMunicipalPropertyCatalog>[0]["scope"];
  throwOnError?: boolean;
}): Promise<void> {
  let refreshError: Error | null = null;
  async function refreshQuery(filters: InvalidateQueryFilters): Promise<void> {
    await queryClient.invalidateQueries(filters);
    const queryList = queryClient.getQueryCache().findAll(filters);
    refreshError ??= queryList.find((query) => query.state.status === "error")?.state.error ?? null;
  }
  async function refreshConsumptionQuery(filters: QueryFilters): Promise<void> {
    queryClient.removeQueries({ ...filters, type: "inactive" });
    await refreshQuery({ ...filters, refetchType: "active" });
    queryClient
      .getQueryCache()
      .findAll(filters)
      .forEach((query: Query): void => {
        if (query.state.status === "error") query.reset();
      });
  }

  await Promise.all([
    refreshQuery({
      queryKey: queryKeys.inventories.lists(),
      refetchType: "all",
      predicate: (query: Query): boolean =>
        query.queryKey.some(
          (part) =>
            typeof part === "object" && part !== null && "workspaceId" in part && part.workspaceId === workspaceId,
        ),
    }),
    refreshQuery({
      queryKey: queryKeys.inventories.details(),
      refetchType: "all",
      predicate: (query: Query): boolean => {
        const inventory: { workspaceId?: string; methodologyId?: string; methodology?: { id?: string } } | undefined =
          query.state.data as
            | { workspaceId?: string; methodologyId?: string; methodology?: { id?: string } }
            | undefined;
        return (
          inventory?.workspaceId === workspaceId &&
          (inventory.methodologyId ?? inventory.methodology?.id) === GHG_ADMINISTRATION_METHODOLOGY_ID
        );
      },
    }),
    refreshConsumptionQuery({
      queryKey: [...queryKeys.municipalProperties.all, "usage"],
      predicate: (query: Query): boolean => propertyIdList.length === 0 && query.meta?.workspaceId === workspaceId,
    }),
    ...propertyIdList.map(
      (propertyId: string): Promise<void> =>
        refreshConsumptionQuery({ queryKey: queryKeys.municipalProperties.usage(propertyId) }),
    ),
    refreshConsumptionQuery({ queryKey: [...queryKeys.municipalPropertyRegisters.lists(), workspaceId] }),
    refreshConsumptionQuery({
      queryKey: queryKeys.municipalPropertyRegisters.details(),
      predicate: (query: Query): boolean => query.meta?.workspaceId === workspaceId,
    }),
    refreshConsumptionQuery({
      queryKey: queryKeys.municipalPropertyYears.all,
      predicate: (query: Query): boolean => query.meta?.workspaceId === workspaceId,
    }),
  ]);
  if (throwOnError && refreshError) throw refreshError;
}
